import { useState } from "react";
import type { ReactNode } from "react";
import MatchDetailTable from "./MatchDetailTable";
import { fmtAed } from "./KpiCard";
import { matchGroupTotals, sigAmount, sigLabel } from "../lib/manualMatchLabels";
import { SIGN_MISMATCH_NOTE } from "../lib/handMatchSign";
import type { HandMatchSignRules } from "../lib/handMatchSign";
import { matchShapeLabel } from "../lib/matchGroups";
import { groupSigs } from "../types/api";
import type { FrameRecords, ManualMatchPair, SideDisplayConfig } from "../types/api";

// One applied / remembered manual match, of ANY shape — 1:1, N:M, or a within-ledger
// offset — as a collapsible row.
//
// Collapsed it names EVERY leg on its own line (book, identity, amount) and foots the two
// sides, so a 3:1 group can be judged without opening anything: the reader is checking
// whether the legs add up to the other side. Expanded it shows every column of every row,
// one table per book, the same tables the statement tabs render.
//
// This replaced a single ellipsised line that concatenated all the legs together. On a
// many-to-one match that line was unreadable — it ran off the row before reaching the
// second side, and it carries at most three fields per leg, so the reference that
// identifies a transaction may not have been in it at all. Removing a match releases real
// rows, so the user has to be able to see exactly what it holds first.
export default function MatchGroupRow({
  pair, criFrame, insFrame, criConfig, insConfig, insurerName, actions, signRules,
}: {
  pair: ManualMatchPair;
  criFrame: FrameRecords;
  insFrame: FrameRecords;
  // How the two books' legs must cancel (result.manual_match_rules).
  signRules?: HandMatchSignRules | null;
  criConfig?: SideDisplayConfig;
  insConfig?: SideDisplayConfig;
  // Names the insurer band in the expanded detail.
  insurerName?: string;
  // Status badges and the row's own control (Remove / Forget), rendered at the far end.
  actions?: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const { cri, ins } = groupSigs(pair);
  const totals = matchGroupTotals(cri, ins, criFrame, insFrame, signRules);
  // Name the SHAPE of the match up front. A within-ledger offset and a cross-book pair
  // move money differently, and an N:M group is the case a reader is most likely to
  // misread as several separate matches.
  //
  // Worded by the SHARED helper, so this badge reads exactly like the Match Group column
  // on the grids and in the workbook — one vocabulary for one concept, whether the match
  // is still a proposal here or already applied out there. The side of a same-book offset
  // is not repeated into the badge; every leg line below already carries its own chip.
  const kind = matchShapeLabel(cri.length, ins.length);

  const leg = (sig: string, side: "CRI" | "Insurer", frame: FrameRecords, cfg?: SideDisplayConfig) => {
    const amt = sigAmount(frame, sig);
    return (
      <span key={sig} className="mgroup-leg">
        <span className="msearch-side">{side}</span>
        <span className="mgroup-leg-text">{sigLabel(frame, sig, cfg)}</span>
        {/* The amount, which sigLabel omits by design — it is what finance checks first
            to judge whether the legs of the group actually offset. */}
        {amt !== null && <span className="msearch-amt">{fmtAed(amt)}</span>}
      </span>
    );
  };

  return (
    <div className="mgroup">
      <div className="mgroup-head">
        <button type="button" className={`mgroup-toggle${open ? " is-open" : ""}`}
          aria-expanded={open}
          title={open ? "Hide the full transaction detail"
                      : "Show every column of every row in this match"}
          onClick={() => setOpen((v) => !v)}>
          <span className="mgroup-caret" aria-hidden="true">▾</span>
          <span className="mgroup-stack">
            <span className="mgroup-kind">
              <span className="badge">{kind}</span>
              <span className="muted mgroup-hint">{open ? "hide detail" : "show all columns"}</span>
            </span>
            {cri.map((s) => leg(s, "CRI", criFrame, criConfig))}
            {ins.map((s) => leg(s, "Insurer", insFrame, insConfig))}
          </span>
        </button>
        {actions && <span className="mgroup-actions">{actions}</span>}
      </div>

      {/* What the two sides foot to. Stated whether or not it ties: a group left as a
          Figure/Balance Mismatch is legitimate, and the residual is the number the
          reader needs. Suppressed entirely when a leg is missing, because every total
          would then be a partial sum. */}
      <div className="mgroup-totals">
        {!totals.resolved ? (
          <span className="muted">
            Totals unavailable — at least one row in this match is not in the files
            uploaded this run.
          </span>
        ) : (
          <>
            <span className="muted">
              {totals.crossBook
                ? <>CRI {fmtAed(totals.criTotal)} ⟷ Insurer {fmtAed(totals.insTotal)} · gap {fmtAed(totals.gap)}</>
                : <>net {fmtAed(totals.net)}</>}
            </span>
            <span className={totals.ties ? "mgroup-verdict is-tied" : "mgroup-verdict"}>
              {totals.ties
                ? "✓ settles exactly"
                : totals.signMismatch
                  ? SIGN_MISMATCH_NOTE
                  : `residual ${fmtAed(totals.residual)}`}
            </span>
          </>
        )}
      </div>

      {open && (
        <div className="mgroup-detail">
          <MatchDetailTable
            criFrame={criFrame} criSigs={cri} criConfig={criConfig}
            insFrame={insFrame} insSigs={ins} insConfig={insConfig}
            insurerName={insurerName} />
        </div>
      )}
    </div>
  );
}
